import { Flex, IconButton, Tabs } from "@chakra-ui/react";
import type { ColumnDef, ColumnFiltersState } from "@tanstack/react-table";
import { createColumnHelper } from "@tanstack/react-table";
import moment from "moment";
import { useState } from "react";
import { FaTimes, FaTrash } from "react-icons/fa";
import { FaCircleCheck } from "react-icons/fa6";

import { useAppSelector } from "../../app/hooks";
import { PaginatedSortableTable } from "../../components/PaginatedSortableTable/PaginatedSortableTable";
import { authSelector } from "../../lib/authentication/authenticationSlice";
import { useDeleteApiKeyMutation, useGetApikeyQuery } from "../artesian/artesianApi";

import { ApiKeyForm } from "./apiKeyModalBodyForm";
import type { ApiKeyType } from "./apiKeyTypes";

export const ApiKeyModalBody = () => {
  const [deleteAction, { isLoading }] = useDeleteApiKeyMutation();
  const [idToRemove, setIdToRemove] = useState<number>(-1);
  const handleApiKeyDelete = async (id: number) => {
    setIdToRemove(id);
    await deleteAction(id).unwrap();
  };
  const columnHelper = createColumnHelper<ApiKeyType>();
  function isActive(date: string | null) {
    if (date === null) return true;
    else if (new Date(date) < new Date()) {
      return false;
    } else if (new Date(date) >= new Date()) {
      return true;
    }
    return false;
  }
  function haveExpirationDate(date: string | null) {
    if (date === null) return false;
    return true;
  }
  const columns: ColumnDef<ApiKeyType>[] = [
    columnHelper.accessor(row => row.Description, {
      id: "Description",
      cell: info => info.getValue(),
      header: "Description",
      meta: { type: "string" },
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor(row => row.ExpiresAt, {
      id: "ExpiresAt",
      cell: info => (haveExpirationDate(info.getValue()) ? moment(info.getValue()).format("YYYY-MM-DD") : "Never"),
      header: "Expiration",
      meta: { type: "string" },
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.display({
      id: "Status",
      header: "Status",
      cell: props =>
        isActive(props.row.original.ExpiresAt) ? <FaCircleCheck color="green" /> : <FaTimes color="red" />,
    }),
    columnHelper.display({
      id: "Action",
      header: "Delete",
      cell: props => (
        <IconButton
          aria-label="Delete"
          variant="ghost"
          size="sm"
          loading={isLoading && idToRemove === props.row.original.Id}
          onClick={async () => {
            await handleApiKeyDelete(props.row.original.Id);
          }}
        >
          <FaTrash />
        </IconButton>
      ),
    }),
  ] as ColumnDef<ApiKeyType>[];
  const userId = useAppSelector(authSelector).data?.userInfo?.email ?? "-";
  const filters = [{ id: "userId", value: userId }] as ColumnFiltersState;

  return (
    <Flex direction="column" gap={2}>
      <Tabs.Root lazyMount unmountOnExit defaultValue="AddKey">
        <Tabs.List colorPalette={"mainTabs"}>
          <Tabs.Trigger value="AddKey">Add API key</Tabs.Trigger>
          <Tabs.Trigger value="KeyList">Key list</Tabs.Trigger>
        </Tabs.List>
        <Tabs.Content value="AddKey">
          <ApiKeyForm />
        </Tabs.Content>
        <Tabs.Content value="KeyList">
          {" "}
          <PaginatedSortableTable<ApiKeyType>
            columns={columns}
            useQueryHook={useGetApikeyQuery}
            externalFilters
            externalFiltersState={filters}
          />
        </Tabs.Content>
      </Tabs.Root>
    </Flex>
  );
};
